import { useEffect, useRef, useState } from 'react';
import { useSessionContext } from '@/auth/useSessionContext';
import { BUSINESS_REQUIREMENT_SITUATION } from '@/consts/businessRequirements';
import {
  getRegistrationStatus,
  hasSaleForRegistrationValidation,
  REGISTRATION_VALIDATION_STATUS,
  resolveRegistrationValidationStatus,
} from '@/sections/@dashboard/identity-verification/registrationValidationStatus';
import useCheckoutReleaseAmount from './useCheckoutReleaseAmount';
import useCompanyData from './useCompanyData';

const SITUATION_BY_STATUS = {
  [REGISTRATION_VALIDATION_STATUS.PRE_REGISTRATION]:
    BUSINESS_REQUIREMENT_SITUATION.PRE_REGISTRATION_PENDING,
  // O suporte pediu para refazer o cadastro: o caminho de volta e o mesmo wizard
  // do pre-cadastro, entao a tela cobra a mesma etapa.
  [REGISTRATION_VALIDATION_STATUS.RESUBMISSION_REQUESTED]:
    BUSINESS_REQUIREMENT_SITUATION.PRE_REGISTRATION_PENDING,
  [REGISTRATION_VALIDATION_STATUS.PENDING]: BUSINESS_REQUIREMENT_SITUATION.VALIDATION_IN_REVIEW,
  [REGISTRATION_VALIDATION_STATUS.REJECTED]: BUSINESS_REQUIREMENT_SITUATION.VALIDATION_REJECTED,
  // Conta bloqueada nao tem variacao propria na Historia. Cai na reprovada porque
  // a acao leva ao Financeiro, que e a tela que ja sabe falar do bloqueio.
  [REGISTRATION_VALIDATION_STATUS.BLOCKED]: BUSINESS_REQUIREMENT_SITUATION.VALIDATION_REJECTED,
};

/**
 * Situação do cadastro para a criação de um novo negócio.
 *
 * RF-02: apta é a conta com pré-cadastro e validação cadastral aprovados. A
 * venda não é requisito de aptidão; ela existe como etapa porque a validação só
 * é liberada depois da primeira venda.
 *
 * Quem responde pelo estágio é `Company.status`, e não o veredito da CAF: o
 * status da conta é o que o Compliance opera, e as duas coisas divergem em
 * linha que importa, como conta bloqueada com KYC aprovado.
 *
 * A venda sai de `user.totalSales` e do valor a liberar, pela mesma regra que
 * libera o botão da CAF. `GET /financial/company/` não devolve venda nenhuma:
 * o serializer traz do usuário só `id` e `email`, então `companyData.totalSales`
 * é sempre indefinido.
 */
export default function useBusinessRequirements() {
  const { user, refreshUser } = useSessionContext();
  const { companyData, isLoadingCompany, hasCompanyDataError, refetchCompanyData } =
    useCompanyData();
  const { checkoutReleaseAmount, isLoadingCheckoutReleaseAmount } = useCheckoutReleaseAmount();

  const [isRefreshingSales, setIsRefreshingSales] = useState(false);
  const hasForcedRefreshRef = useRef(false);

  const validationStatus = resolveRegistrationValidationStatus({
    registrationStatus: getRegistrationStatus({ companyData }),
  });

  const hasSale = hasSaleForRegistrationValidation({
    totalSales: user?.totalSales,
    releaseAmount: checkoutReleaseAmount,
  });

  // Mesma precaucao da tela da CAF: `totalSales` vem da sessao, buscada no
  // login, e envelhece justamente em quem acabou de vender. Antes de dizer que
  // falta venda, atualiza o usuario uma vez.
  const shouldRefreshBeforeNoSale =
    !hasSale && validationStatus === REGISTRATION_VALIDATION_STATUS.NOT_STARTED;

  useEffect(() => {
    if (!shouldRefreshBeforeNoSale || hasForcedRefreshRef.current) return;

    hasForcedRefreshRef.current = true;
    setIsRefreshingSales(true);
    Promise.resolve(refreshUser({ forceUpdate: true }))
      .catch(() => {})
      .finally(() => setIsRefreshingSales(false));
  }, [refreshUser, shouldRefreshBeforeNoSale]);

  const isLoading = isLoadingCompany || isLoadingCheckoutReleaseAmount || isRefreshingSales;

  const resolveSituation = () => {
    if (validationStatus === REGISTRATION_VALIDATION_STATUS.NOT_STARTED) {
      return hasSale
        ? BUSINESS_REQUIREMENT_SITUATION.VALIDATION_NOT_STARTED
        : BUSINESS_REQUIREMENT_SITUATION.AWAITING_FIRST_SALE;
    }

    return SITUATION_BY_STATUS[validationStatus] ?? null;
  };

  const canCreateBusiness = validationStatus === REGISTRATION_VALIDATION_STATUS.APPROVED;
  const situation = canCreateBusiness ? null : resolveSituation();

  return {
    isLoading,
    // Status fora do mapa nao vira tela chutada: sem situacao conhecida o front
    // trata como falha de consulta e oferece nova tentativa, que e o 6.1.
    isError: !isLoading && (hasCompanyDataError || (!canCreateBusiness && !situation)),
    canCreateBusiness,
    situation,
    refetch: refetchCompanyData,
  };
}
